import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Close} from '@ELib/assets/images';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';

import {
  EButton,
  ECancelButton,
  EDangerButton,
  EIconButton,
} from '../../EButtons';
import {EDialog} from '../../EDialog';
import {ColumnElement} from '../columnState';

import {ColumnSelection} from './ColumnSelection';
import {
  ButtonRow,
  CloseButtonWrapper,
  DialogContent,
  DialogHeading,
  WarningText,
} from './ColumnsCustomizeDialog.styles';
import {SortElement} from './ColumnsCustomizeDialog.types';

export function ColumnsCustomizeDialog({
  columnItems,
  onDialogClose,
}: {
  columnItems: SortElement[];
  onDialogClose: (newActiveColumns?: ColumnElement[]) => void;
}): React.ReactElement {
  const {t} = useTranslation('lib');
  const [showWarning, setShowWarning] = useState(false);
  const [internalItems, setInternalItems] = useState<
    Record<'active' | 'inactive', SortElement[]>
  >({active: [], inactive: []});

  useMountEffect(() => {
    const activeItems = columnItems.filter((item) => item.active);
    const inactiveItems = columnItems.filter((item) => !item.active);
    setInternalItems({active: activeItems, inactive: inactiveItems});
  });

  const handleResetButtonClick = () => {
    const activeItems = columnItems.filter((item) => item.defaultActive);
    const inactiveItems = columnItems.filter((item) => !item.defaultActive);
    setInternalItems({active: activeItems, inactive: inactiveItems});
  };

  const handleSubmit = () => {
    if (internalItems.active.length < 1) {
      setShowWarning(true);
      return;
    }
    onDialogClose([
      ...internalItems.active.map((item) => ({
        name: item.id,
        active: true,
        defaultHidden: !item.defaultActive,
      })),
      ...internalItems.inactive.map((item) => ({
        name: item.id,
        active: false,
        defaultHidden: !item.defaultActive,
      })),
    ]);
  };

  useEffect(() => {
    if (internalItems.active.length > 0 && showWarning) {
      setShowWarning(false);
    }
  }, [internalItems.active.length, showWarning]);

  return (
    <EDialog open onClose={() => onDialogClose()}>
      <DialogContent data-testid='ETable-columnsDialog-content'>
        <DialogHeading>
          {t('columnsDialog.header')}
          <CloseButtonWrapper>
            <EIconButton onClick={() => onDialogClose()}>
              <Close />
            </EIconButton>
          </CloseButtonWrapper>
        </DialogHeading>
        {t('columnsDialog.descriptionText')}
        <ColumnSelection
          internalItems={internalItems}
          setInternalItems={setInternalItems}
        />
        {showWarning ? (
          <WarningText>{t('columnsDialog.warningText')}</WarningText>
        ) : null}
        <ButtonRow>
          <ECancelButton onClick={() => onDialogClose()} />
          <ButtonRow>
            <EDangerButton onClick={handleResetButtonClick}>
              {t('columnsDialog.actions.reset')}
            </EDangerButton>
            <EButton testId='applyColumnsChanges' onClick={handleSubmit}>
              {t('actions.apply')}
            </EButton>
          </ButtonRow>
        </ButtonRow>
      </DialogContent>
    </EDialog>
  );
}
